import { http } from './http';

const BASE = '/v1/instructor';

export type PushState = 'unsupported' | 'denied' | 'default' | 'subscribed';

/** Le navigateur supporte-t-il les notifications push ? */
export function pushSupported(): boolean {
  return (
    'serviceWorker' in navigator &&
    'PushManager' in window &&
    'Notification' in window
  );
}

function urlBase64ToUint8Array(base64: string): Uint8Array {
  const padding = '='.repeat((4 - (base64.length % 4)) % 4);
  const b64 = (base64 + padding).replace(/-/g, '+').replace(/_/g, '/');
  const raw = window.atob(b64);
  const out = new Uint8Array(raw.length);
  for (let i = 0; i < raw.length; i += 1) out[i] = raw.charCodeAt(i);
  return out;
}

async function fetchPublicKey(): Promise<string | null> {
  const { data } = await http.get<{ publicKey: string | null }>(
    `${BASE}/me/push/public-key`,
  );
  return data.publicKey;
}

/** État courant de l'abonnement push. */
export async function getPushState(): Promise<PushState> {
  if (!pushSupported()) return 'unsupported';
  if (Notification.permission === 'denied') return 'denied';
  const reg = await navigator.serviceWorker.ready.catch(() => null);
  const sub = reg ? await reg.pushManager.getSubscription() : null;
  if (sub && Notification.permission === 'granted') return 'subscribed';
  return 'default';
}

/** Demande la permission et abonne l'appareil aux notifications. */
export async function enablePush(): Promise<PushState> {
  if (!pushSupported()) return 'unsupported';
  const permission = await Notification.requestPermission();
  if (permission !== 'granted') return 'denied';

  const key = await fetchPublicKey();
  if (!key) throw new Error('Notifications non configurées côté serveur.');

  const reg = await navigator.serviceWorker.ready;
  const existing = await reg.pushManager.getSubscription();
  const sub =
    existing ??
    (await reg.pushManager.subscribe({
      userVisibleOnly: true,
      applicationServerKey: urlBase64ToUint8Array(key) as unknown as BufferSource,
    }));

  await http.post(`${BASE}/me/push/subscribe`, { subscription: sub.toJSON() });
  return 'subscribed';
}

/** Désabonne l'appareil des notifications. */
export async function disablePush(): Promise<PushState> {
  const reg = await navigator.serviceWorker.ready.catch(() => null);
  const sub = reg ? await reg.pushManager.getSubscription() : null;
  if (sub) {
    await http
      .post(`${BASE}/me/push/unsubscribe`, { endpoint: sub.endpoint })
      .catch(() => undefined);
    await sub.unsubscribe().catch(() => undefined);
  }
  return 'default';
}

/** Envoie une notification de test à soi-même. */
export async function sendTestPush(): Promise<void> {
  await http.post(`${BASE}/me/push/test`);
}
